// Helpers pour interpréter les erreurs 402 (Lot 8.4).
//
// Le back renvoie pour les refus de quota :
//   { detail: { message: "...", reason: "trial_expired" | ... } }
//
// Le wrapper `ApiError` du client conserve le payload complet dans `body`,
// mais sa propriété `detail` n'est qu'une string (fallback générique quand
// `payload.detail` n'est pas une string). On extrait donc le `reason` et
// le `message` du body brut, sans toucher à la logique du client.

import { ApiError } from "./client";
import type { QuotaRefusalReason } from "./types";

/** Erreur quota typée + message utilisateur prêt à afficher. */
export type QuotaRefusal = {
  reason: QuotaRefusalReason;
  message: string;
};

const KNOWN_REASONS: ReadonlySet<QuotaRefusalReason> = new Set([
  "trial_expired",
  "invoice_quota_exceeded",
  "seats_quota_exceeded",
]);

function isKnownReason(value: unknown): value is QuotaRefusalReason {
  return typeof value === "string" && KNOWN_REASONS.has(value as QuotaRefusalReason);
}

/**
 * Tente de reconnaître un refus 402 structuré. Retourne `null` si l'erreur
 * n'est pas une 402 ou si le payload n'a pas la forme attendue — l'appelant
 * tombera sur son chemin d'erreur générique.
 */
export function parseQuotaRefusal(err: unknown): QuotaRefusal | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 402) return null;
  const body = err.body as { detail?: unknown } | null;
  const detail = body?.detail;
  if (!detail || typeof detail !== "object") return null;
  const reason = (detail as { reason?: unknown }).reason;
  const message = (detail as { message?: unknown }).message;
  if (!isKnownReason(reason)) return null;
  return {
    reason,
    message: typeof message === "string" && message.length > 0
      ? message
      : defaultMessage(reason),
  };
}

function defaultMessage(reason: QuotaRefusalReason): string {
  switch (reason) {
    case "trial_expired":
      return "Votre essai est terminé.";
    case "invoice_quota_exceeded":
      return "Quota mensuel de factures atteint.";
    case "seats_quota_exceeded":
      return "Nombre de sièges du plan atteint.";
  }
}

/** Libellé de la CTA selon la raison — toujours pointe vers /pricing. */
export function quotaCtaLabel(reason: QuotaRefusalReason): string {
  switch (reason) {
    case "trial_expired":
      return "Choisir un plan";
    case "invoice_quota_exceeded":
      return "Passer au plan supérieur";
    case "seats_quota_exceeded":
      return "Passer au plan supérieur";
  }
}
